import { useEffect, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const LS_LANG = 'keystone_app_lang';
const LS_CACHE = 'keystone_i18n_cache_';   // per-lang: {src: dst}
const LS_VER   = 'keystone_i18n_ver_';     // per-lang: server ETag/version

export const getAppLang = () => localStorage.getItem(LS_LANG) || 'en';
export const setAppLang = (l) => { localStorage.setItem(LS_LANG, l); window.location.reload(); };

// Skip: numbers, codes (B-1042), emails, short tokens, URLs
const translatable = (s) => {
  const t = s.trim();
  if (t.length < 2 || t.length > 400) return false;
  if (!/[a-zA-Z]{2}/.test(t)) return false;
  if (/^[A-Z0-9\-_/.:#€£$%\s]+$/.test(t) && !/[a-z]/.test(t)) return false;
  if (/@|https?:\/\//.test(t)) return false;
  return true;
};

/**
 * DomTranslator, runtime translation layer for the app (100+ screens,
 * no i18n retrofit needed).
 *
 * Strategy (updated 2026-02-11):
 *   1. Load the FULL cached bundle for the target language from
 *      localStorage on mount, apply to DOM synchronously, zero network.
 *   2. In the background, fetch /api/translations/bundle/{lang} with
 *      an ETag conditional GET. If the server has more translations
 *      than localStorage, merge and re-apply. 304 = no-op.
 *   3. Fall back to /runtime-batch only for genuine misses that aren't
 *      in the marketing corpus (e.g. app screens post-login).
 *
 * This eliminates the "flash of English → translated" flicker on repeat
 * visits and removes the need for a redeploy every time the admin
 * completes a translation job.
 */
export default function DomTranslator({ rootId = 'root' }) {
  const mapRef = useRef(null);
  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    const lang = getAppLang();
    if (lang === 'en') return undefined;

    // ─── 1. Hydrate from localStorage synchronously ───
    let cache = {};
    try { cache = JSON.parse(localStorage.getItem(LS_CACHE + lang) || '{}'); } catch { /* noop */ }
    mapRef.current = cache;

    const applyToNode = (node) => {
      const src = node.__i18nSrc || node.nodeValue;
      if (!src) return;
      const trimmed = src.trim();
      const dst = mapRef.current[trimmed];
      if (!dst) return;
      const next = src.replace(trimmed, dst);
      if (node.nodeValue !== next) {
        node.__i18nSrc = src;
        node.nodeValue = next;
      }
    };

    const collect = (root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => {
          const p = n.parentElement;
          if (!p || ['SCRIPT', 'STYLE', 'NOSCRIPT', 'INPUT', 'TEXTAREA'].includes(p.tagName)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let node = walker.nextNode();
      while (node) {
        const src = (node.__i18nSrc || node.nodeValue || '').trim();
        if (src && translatable(src)) {
          if (mapRef.current[src]) applyToNode(node);
          else pendingRef.current.add(src);
        }
        node = walker.nextNode();
      }
    };

    // ─── 2. Background fetch of the full bundle (ETag-cached) ───
    const refreshBundle = async () => {
      const currentVer = localStorage.getItem(LS_VER + lang) || '';
      try {
        const res = await fetch(`${API}/api/translations/bundle/${lang}`, {
          method: 'GET',
          headers: currentVer ? { 'If-None-Match': `W/"${currentVer}"` } : {},
        });
        if (res.status === 304) return;  // localStorage is up-to-date
        if (!res.ok) return;
        const data = await res.json();
        const incoming = data.translations || {};
        // Merge server bundle into local cache
        Object.assign(mapRef.current, incoming);
        try {
          localStorage.setItem(LS_CACHE + lang, JSON.stringify(mapRef.current));
          if (data.version) localStorage.setItem(LS_VER + lang, data.version);
        } catch { /* quota, ignore */ }
        // Re-apply to whatever's on screen
        const root = rootRef.current || document.getElementById(rootId) || document.body;
        collect(root);
      } catch { /* offline is fine, localStorage still works */ }
    };

    // ─── 3. Only spend LLM budget on genuine misses ───
    const flushMisses = async () => {
      const batch = [...pendingRef.current].slice(0, 150);
      if (!batch.length) return;
      pendingRef.current = new Set([...pendingRef.current].slice(150));
      try {
        const res = await fetch(`${API}/api/translations/runtime-batch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
          body: JSON.stringify({ lang, strings: batch }),
        });
        if (!res.ok) return;
        const data = await res.json();
        const additions = data.translations || {};
        if (!Object.keys(additions).length) return;
        Object.assign(mapRef.current, additions);
        try { localStorage.setItem(LS_CACHE + lang, JSON.stringify(mapRef.current)); } catch { /* quota */ }
        const root = rootRef.current || document.getElementById(rootId) || document.body;
        collect(root);
      } catch { /* retry on next mutation */ }
    };

    // Debounce the misses flush, bundle-first cache should already
    // cover the page, so this is now a distant fallback rather than
    // a critical path.
    const scheduleMisses = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(flushMisses, 1200);
    };

    const root = document.getElementById(rootId) || document.body;
    rootRef.current = root;

    // Initial synchronous apply from localStorage
    collect(root);

    // Kick the background refresh (non-blocking)
    refreshBundle();

    // Fallback flush for anything still missing after the bundle merged
    scheduleMisses();

    // Watch for SPA navigation / component mounts
    const obs = new MutationObserver((muts) => {
      let dirty = false;
      for (const m of muts) {
        if (m.type === 'childList' && m.addedNodes.length) { dirty = true; break; }
        if (m.type === 'characterData') { dirty = true; break; }
      }
      if (dirty) { collect(root); scheduleMisses(); }
    });
    obs.observe(root, { childList: true, subtree: true, characterData: true });
    return () => { obs.disconnect(); clearTimeout(timerRef.current); };
  }, [rootId]);

  return null;
}
